/**
 * Owner: Masa Bayu
 * File: qx-draft-image.ts
 * Date:Time: 2026-10-16 01:10
 * Description: Draft image float · drag · resize · style · description.
 */

import {
  autoScrollDraftCanvas,
  placeDraftFigure,
} from './qx-draft-image-place';
import {
  beginDraftFigureNote,
  DRAFT_FIGURE_NOTE_CLASS,
  normalizeDraftFigureNote,
} from './qx-draft-image-note';
import { normalizeDraftFigureStyle } from './qx-draft-image-style';

export type {
  DraftFigureFrame,
  DraftFigureShadow,
  DraftFigurePreset,
  DraftFigurePicture,
} from './qx-draft-image-style';
export {
  runDraftFigureFrame,
  runDraftFigureShadow,
  runDraftFigurePreset,
} from './qx-draft-image-style';
export { htmlFromDraftFigure } from './qx-draft-image-html';

export type DraftFigureFloat = 'left' | 'right' | 'none';
type ResizeKind = 'e' | 's' | 'se';

const FIGURE_CLASS = 'qxw-draft-figure';
const SELECTED_CLASS = 'qxw-draft-figure--selected';
const HANDLE_CLASS = 'qxw-draft-figure__handle';
const DELETE_CLASS = 'qxw-draft-figure__delete';
const MIN_WIDTH_PX = 48;
const MIN_HEIGHT_PX = 32;
const DRAG_THRESHOLD_PX = 5;
const DEFAULT_FLOAT: DraftFigureFloat = 'left';
const RESIZE_KINDS: readonly ResizeKind[] = ['e', 's', 'se'];

export function parseDraftFigureFloat(raw: string | null | undefined): DraftFigureFloat | null {
  if (raw === 'left' || raw === 'right' || raw === 'none') return raw;
  return null;
}

function isBlankBlockEl(el: HTMLElement): boolean {
  const tag = el.tagName.toLowerCase();
  if (tag !== 'p' && tag !== 'div') return false;
  const text = (el.textContent ?? '').replace(/\u00a0/g, ' ').trim();
  return text.length === 0;
}

/**
 * Remove empty Enter blocks between a float and the next body text.
 * Those spacers sit in the wrap column and invent a top gap beside the image.
 */
export function clearBlankAfterFloat(figure: HTMLElement): void {
  const side = parseDraftFigureFloat(figure.getAttribute('data-qx-float'));
  if (side !== 'left' && side !== 'right') return;
  let next = figure.nextElementSibling;
  while (next instanceof HTMLElement && isBlankBlockEl(next)) {
    const following = next.nextElementSibling;
    // Keep a trailing empty block when nothing follows (caret sink).
    if (!following) break;
    const remove = next;
    next = following;
    remove.remove();
  }
}

/** Float so following paragraphs wrap beside the image. */
export function applyDraftFigureFloat(figure: HTMLElement, side: DraftFigureFloat): void {
  figure.setAttribute('data-qx-float', side);
  figure.classList.toggle('qxw-draft-figure--float-left', side === 'left');
  figure.classList.toggle('qxw-draft-figure--float-right', side === 'right');
  figure.classList.toggle('qxw-draft-figure--float-none', side === 'none');
  if (side === 'left' || side === 'right') clearBlankAfterFloat(figure);
}

/** Apply wrap to the selected figure (or last figure in the editor). */
export function runDraftFigureFloat(side: DraftFigureFloat): boolean {
  const editor = document.querySelector('.qxw-page__editor');
  if (!(editor instanceof HTMLElement)) return false;
  const selected = editor.querySelector(`.${SELECTED_CLASS}`);
  const figure =
    selected instanceof HTMLElement && selected.classList.contains(FIGURE_CLASS)
      ? selected
      : editor.querySelector(`.${FIGURE_CLASS}`);
  if (!(figure instanceof HTMLElement)) return false;
  applyDraftFigureFloat(figure, side);
  selectFigure(editor, figure);
  return true;
}

/** Remove selected draft figure (or last figure). */
export function runDraftFigureDelete(): boolean {
  const editor = document.querySelector('.qxw-page__editor');
  if (!(editor instanceof HTMLElement)) return false;
  const selected = editor.querySelector(`.${SELECTED_CLASS}`);
  const figure =
    selected instanceof HTMLElement && selected.classList.contains(FIGURE_CLASS)
      ? selected
      : editor.querySelector(`.${FIGURE_CLASS}`);
  if (!(figure instanceof HTMLElement)) return false;
  figure.remove();
  return true;
}

function editorRoot(node: Node | null): HTMLElement | null {
  if (!(node instanceof HTMLElement)) return null;
  return node.closest('.qxw-page__editor');
}

function clearSelection(editor: HTMLElement): void {
  editor.querySelectorAll(`.${SELECTED_CLASS}`).forEach((el) => el.classList.remove(SELECTED_CLASS));
}

function selectFigure(editor: HTMLElement, figure: HTMLElement): void {
  clearSelection(editor);
  figure.classList.add(SELECTED_CLASS);
}

function ensureChrome(figure: HTMLElement): void {
  for (const kind of RESIZE_KINDS) {
    if (figure.querySelector(`[data-qx-resize="${kind}"]`)) continue;
    const handle = document.createElement('span');
    handle.className = `${HANDLE_CLASS} ${HANDLE_CLASS}--${kind}`;
    handle.setAttribute('data-qx-resize', kind);
    handle.contentEditable = 'false';
    figure.appendChild(handle);
  }
  if (!figure.querySelector(`.${DELETE_CLASS}`)) {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = DELETE_CLASS;
    btn.setAttribute('aria-label', 'Delete image');
    btn.title = 'Delete image';
    btn.textContent = '×';
    btn.contentEditable = 'false';
    figure.appendChild(btn);
  }
}

/** Set width and/or height independently (no aspect lock). */
export function applyDraftImageBox(
  img: HTMLImageElement,
  widthPx: number | null,
  heightPx: number | null,
): void {
  if (widthPx !== null) {
    const w = Math.max(MIN_WIDTH_PX, Math.round(widthPx));
    img.style.width = `${w}px`;
    img.setAttribute('data-qx-width', String(w));
  }
  if (heightPx !== null) {
    const h = Math.max(MIN_HEIGHT_PX, Math.round(heightPx));
    img.style.height = `${h}px`;
    img.setAttribute('data-qx-height', String(h));
  }
  img.style.objectFit = 'fill';
}

function restoreBox(img: HTMLImageElement): void {
  const wRaw = img.getAttribute('data-qx-width') || img.style.width;
  const hRaw = img.getAttribute('data-qx-height') || img.style.height;
  const w = wRaw ? Number.parseFloat(wRaw) : NaN;
  const h = hRaw && hRaw !== 'auto' ? Number.parseFloat(hRaw) : NaN;
  applyDraftImageBox(
    img,
    Number.isFinite(w) && w > 0 ? w : null,
    Number.isFinite(h) && h > 0 ? h : null,
  );
}

/** Wrap / decorate draft images (idempotent). */
export function enhanceDraftImages(root: HTMLElement): void {
  const imgs = Array.from(root.querySelectorAll('img'));
  for (const img of imgs) {
    if (!(img instanceof HTMLImageElement)) continue;
    let figure = img.closest('figure');
    if (!figure) {
      figure = document.createElement('figure');
      figure.className = FIGURE_CLASS;
      figure.contentEditable = 'false';
      figure.draggable = false;
      img.replaceWith(figure);
      figure.appendChild(img);
    } else {
      figure.classList.add(FIGURE_CLASS);
      figure.contentEditable = 'false';
      figure.draggable = false;
    }
    img.draggable = false;
    restoreBox(img);
    const float =
      parseDraftFigureFloat(figure.getAttribute('data-qx-float')) ?? DEFAULT_FLOAT;
    applyDraftFigureFloat(figure, float);
    ensureChrome(figure);
    normalizeDraftFigureNote(figure);
    normalizeDraftFigureStyle(figure);
  }
}

function insertNodeAtSelection(node: Node): boolean {
  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0) {
    const editor = document.querySelector('.qxw-page__editor');
    if (!(editor instanceof HTMLElement)) return false;
    editor.appendChild(node);
    return true;
  }
  const range = selection.getRangeAt(0);
  range.deleteContents();
  range.insertNode(node);
  range.setStartAfter(node);
  range.collapse(true);
  selection.removeAllRanges();
  selection.addRange(range);
  return true;
}

/** Insert a resizable / movable figure at the caret. */
export function insertDraftImageFigure(src: string): boolean {
  if (!src) return false;
  const figure = document.createElement('figure');
  figure.className = FIGURE_CLASS;
  figure.contentEditable = 'false';
  figure.draggable = false;
  applyDraftFigureFloat(figure, DEFAULT_FLOAT);
  const img = document.createElement('img');
  img.src = src;
  img.alt = 'Image';
  img.draggable = false;
  figure.appendChild(img);
  ensureChrome(figure);
  if (!insertNodeAtSelection(figure)) return false;
  const editor = editorRoot(figure);
  if (editor) selectFigure(editor, figure);
  return true;
}

/**
 * Bind select · pointer drag (Y reorder · X float side) · resize · delete.
 * Returns cleanup.
 */
export function bindDraftImageInteractions(
  root: HTMLElement,
  onChange: () => void,
): () => void {
  enhanceDraftImages(root);
  let resizing: {
    kind: ResizeKind;
    img: HTMLImageElement;
    figure: HTMLElement;
    startX: number;
    startY: number;
    startW: number;
    startH: number;
  } | null = null;
  let moving: {
    figure: HTMLElement;
    startX: number;
    startY: number;
    active: boolean;
    moved: boolean;
  } | null = null;
  /** Only this figure may be removed by Backspace/Delete (not while editing text). */
  let keyboardFigure: HTMLElement | null = null;

  const armKeyboardFigure = (figure: HTMLElement | null) => {
    keyboardFigure = figure;
  };

  const onPointerDown = (event: PointerEvent) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const del = target.closest(`.${DELETE_CLASS}`);
    if (del && root.contains(del)) {
      const figure = del.closest('figure');
      if (!(figure instanceof HTMLElement)) return;
      event.preventDefault();
      event.stopPropagation();
      armKeyboardFigure(null);
      figure.remove();
      onChange();
      return;
    }

    const handle = target.closest(`.${HANDLE_CLASS}`);
    if (handle && root.contains(handle)) {
      const figure = handle.closest('figure');
      const img = figure?.querySelector('img');
      const kind = parseResizeKind(handle.getAttribute('data-qx-resize'));
      if (!(figure instanceof HTMLElement) || !(img instanceof HTMLImageElement) || !kind) return;
      event.preventDefault();
      event.stopPropagation();
      selectFigure(root, figure);
      armKeyboardFigure(figure);
      const box = img.getBoundingClientRect();
      resizing = {
        kind,
        img,
        figure,
        startX: event.clientX,
        startY: event.clientY,
        startW: box.width || img.naturalWidth || 200,
        startH: box.height || img.naturalHeight || 120,
      };
      applyDraftImageBox(img, resizing.startW, resizing.startH);
      handle.setPointerCapture?.(event.pointerId);
      return;
    }

    const figure = target.closest('figure');
    if (figure instanceof HTMLElement && root.contains(figure) && figure.classList.contains(FIGURE_CLASS)) {
      selectFigure(root, figure);
      /* Description under image · open composer · no drag. */
      if (target.closest(`.${DRAFT_FIGURE_NOTE_CLASS}, .qxw-draft-figure__note, figcaption`)) {
        armKeyboardFigure(null);
        beginDraftFigureNote(figure);
        return;
      }
      armKeyboardFigure(figure);
      moving = {
        figure,
        startX: event.clientX,
        startY: event.clientY,
        active: false,
        moved: false,
      };
      figure.setPointerCapture?.(event.pointerId);
      return;
    }
    if (!target.closest(`.${FIGURE_CLASS}`)) {
      clearSelection(root);
      armKeyboardFigure(null);
    }
  };

  const onPointerMove = (event: PointerEvent) => {
    if (resizing) {
      event.preventDefault();
      const maxW = Math.max(MIN_WIDTH_PX, root.clientWidth - 8);
      const dx = event.clientX - resizing.startX;
      const dy = event.clientY - resizing.startY;
      const nextW = Math.min(maxW, Math.max(MIN_WIDTH_PX, resizing.startW + dx));
      const nextH = Math.max(MIN_HEIGHT_PX, resizing.startH + dy);
      if (resizing.kind === 'e') applyDraftImageBox(resizing.img, nextW, null);
      else if (resizing.kind === 's') applyDraftImageBox(resizing.img, null, nextH);
      else applyDraftImageBox(resizing.img, nextW, nextH);
      return;
    }

    if (!moving) return;
    const dx = event.clientX - moving.startX;
    const dy = event.clientY - moving.startY;
    if (!moving.active) {
      if (Math.hypot(dx, dy) < DRAG_THRESHOLD_PX) return;
      moving.active = true;
      moving.figure.classList.add('qxw-draft-figure--dragging');
    }
    event.preventDefault();
    autoScrollDraftCanvas(root, event.clientY);
    placeDraftFigure(
      root,
      moving.figure,
      event.clientX,
      event.clientY,
      applyDraftFigureFloat,
    );
    moving.moved = true;
  };

  const onPointerUp = () => {
    if (resizing) {
      resizing = null;
      onChange();
      return;
    }
    if (moving) {
      moving.figure.classList.remove('qxw-draft-figure--dragging');
      if (moving.moved) {
        selectFigure(root, moving.figure);
        onChange();
      }
      moving = null;
    }
  };

  const releaseImageKeyboard = () => {
    if (!keyboardFigure) return;
    clearSelection(root);
    armKeyboardFigure(null);
  };

  const onKeyDown = (event: KeyboardEvent) => {
    // Body text editing → never let a stale image selection steal keys.
    if (event.key === 'Enter' || (event.key.length === 1 && !event.metaKey && !event.ctrlKey)) {
      releaseImageKeyboard();
      return;
    }
    if (event.key.startsWith('Arrow')) {
      // After caret moves into text, release image target.
      requestAnimationFrame(() => {
        const sel = window.getSelection();
        const node = sel?.anchorNode;
        if (!keyboardFigure || !node) return;
        if (keyboardFigure === node || keyboardFigure.contains(node)) return;
        releaseImageKeyboard();
      });
      return;
    }
    if (event.key !== 'Backspace' && event.key !== 'Delete') return;

    // Only when the image itself is the keyboard target (clicked/selected).
    // Do NOT use CSS --selected alone — that stole Backspace while typing.
    const figure =
      keyboardFigure && root.contains(keyboardFigure) ? keyboardFigure : null;
    if (!figure || !figure.classList.contains(FIGURE_CLASS)) return;

    const sel = window.getSelection();
    if (sel && !sel.isCollapsed) return;

    event.preventDefault();
    event.stopPropagation();
    armKeyboardFigure(null);
    figure.remove();
    onChange();
  };

  root.addEventListener('pointerdown', onPointerDown);
  window.addEventListener('pointermove', onPointerMove);
  window.addEventListener('pointerup', onPointerUp);
  root.addEventListener('keydown', onKeyDown, true);

  return () => {
    root.removeEventListener('pointerdown', onPointerDown);
    window.removeEventListener('pointermove', onPointerMove);
    window.removeEventListener('pointerup', onPointerUp);
    root.removeEventListener('keydown', onKeyDown, true);
  };
}

function parseResizeKind(raw: string | null): ResizeKind | null {
  if (raw === 'e' || raw === 's' || raw === 'se') return raw;
  return null;
}
